// In-memory state management for zones and pins

import type { Zone, ContextPin, ZoneState, ZoneType, PinType } from '@/types/zone'
import { getZoneTypeDef } from '@/constants/zoneTypes'
import { debug } from '@/utils/debug'

export type ZoneStateChangeCallback = (state: ZoneState) => void

let nextId = 1

function generateId(prefix: string): string {
  return `${prefix}_${nextId++}`
}

/**
 * Zone state manager - manages zones and pins in memory
 */
class ZoneStateManager {
  private zones: Map<string, Zone> = new Map()
  private pins: Map<string, ContextPin> = new Map()
  private changeCallbacks: ZoneStateChangeCallback[] = []

  constructor() {
    debug.log('ZoneState', 'ZoneStateManager initialized')
  }

  // ============================================================================
  // Zone operations
  // ============================================================================

  /**
   * Add a new zone
   */
  addZone(
    zoneType: ZoneType,
    x: number,
    y: number,
    width: number,
    height: number,
    options?: Partial<Omit<Zone, 'id' | 'type' | 'zoneType' | 'x' | 'y' | 'width' | 'height'>>
  ): string {
    const typeDef = getZoneTypeDef(zoneType)
    const id = generateId('zone')

    const zone: Zone = {
      id,
      type: 'zone',
      zoneType,
      x,
      y,
      width,
      height,
      rotation: options?.rotation ?? 0,
      elevation: options?.elevation ?? typeDef.defaultHeight,
      shapeKind: options?.shapeKind,
      label: options?.label,
      notes: options?.notes,
      points: options?.points,
      pathWaypoints: options?.pathWaypoints,
      pathWidth: options?.pathWidth,
    }

    // Auto-compute width/height from polygon bounding box
    if (zone.points && zone.points.length >= 3) {
      const xs = zone.points.map(p => p.x)
      const ys = zone.points.map(p => p.y)
      zone.width = Math.max(...xs) - Math.min(...xs)
      zone.height = Math.max(...ys) - Math.min(...ys)
    }

    this.zones.set(id, zone)
    debug.log('ZoneState', `Added zone: ${id} (${zoneType}) at (${x}, ${y})`)
    this.notifyChange()
    return id
  }

  /**
   * Update an existing zone
   */
  updateZone(id: string, updates: Partial<Omit<Zone, 'id' | 'type'>>): void {
    const zone = this.zones.get(id)
    if (!zone) {
      debug.warn('ZoneState', `Zone not found: ${id}`)
      return
    }

    const updatedZone = { ...zone, ...updates }

    // Auto-compute width/height from polygon bounding box
    if (updatedZone.points && updatedZone.points.length >= 3) {
      const xs = updatedZone.points.map(p => p.x)
      const ys = updatedZone.points.map(p => p.y)
      updatedZone.width = Math.max(...xs) - Math.min(...xs)
      updatedZone.height = Math.max(...ys) - Math.min(...ys)
    }

    this.zones.set(id, updatedZone)
    debug.log('ZoneState', `Updated zone: ${id}`, updates)
    this.notifyChange()
  }

  /**
   * Restore a zone with its original ID (used by undo)
   */
  restoreZone(zone: Zone): void {
    this.zones.set(zone.id, { ...zone })
    // Ensure nextId stays ahead
    const idNum = parseInt(zone.id.split('_')[1] || '0', 10)
    if (idNum >= nextId) {
      nextId = idNum + 1
    }
    debug.log('ZoneState', `Restored zone: ${zone.id}`)
    this.notifyChange()
  }

  /**
   * Delete a zone
   */
  deleteZone(id: string): boolean {
    const deleted = this.zones.delete(id)
    if (deleted) {
      debug.log('ZoneState', `Deleted zone: ${id}`)
      this.notifyChange()
    } else {
      debug.warn('ZoneState', `Zone not found for deletion: ${id}`)
    }
    return deleted
  }

  /**
   * Get all zones
   */
  getZones(): Zone[] {
    return Array.from(this.zones.values())
  }

  /**
   * Get a zone by ID
   */
  getZoneById(id: string): Zone | undefined {
    return this.zones.get(id)
  }

  // ============================================================================
  // Pin operations
  // ============================================================================

  /**
   * Add a new pin
   */
  addPin(
    pinType: PinType,
    x: number,
    y: number,
    options?: Partial<Omit<ContextPin, 'id' | 'type' | 'pinType' | 'x' | 'y'>>
  ): string {
    const id = generateId('pin')

    const pin: ContextPin = {
      id,
      type: 'pin',
      pinType,
      x,
      y,
      label: options?.label,
      photoUrl: options?.photoUrl,
      notes: options?.notes,
    }

    this.pins.set(id, pin)
    debug.log('ZoneState', `Added pin: ${id} (${pinType}) at (${x}, ${y})`)
    this.notifyChange()
    return id
  }

  /**
   * Update an existing pin
   */
  updatePin(id: string, updates: Partial<Omit<ContextPin, 'id' | 'type'>>): void {
    const pin = this.pins.get(id)
    if (!pin) {
      debug.warn('ZoneState', `Pin not found: ${id}`)
      return
    }

    const updatedPin = { ...pin, ...updates }
    this.pins.set(id, updatedPin)
    debug.log('ZoneState', `Updated pin: ${id}`, updates)
    this.notifyChange()
  }

  /**
   * Restore a pin with its original ID (used by undo)
   */
  restorePin(pin: ContextPin): void {
    this.pins.set(pin.id, { ...pin })
    // Ensure nextId stays ahead
    const idNum = parseInt(pin.id.split('_')[1] || '0', 10)
    if (idNum >= nextId) {
      nextId = idNum + 1
    }
    debug.log('ZoneState', `Restored pin: ${pin.id}`)
    this.notifyChange()
  }

  /**
   * Delete a pin
   */
  deletePin(id: string): boolean {
    const deleted = this.pins.delete(id)
    if (deleted) {
      debug.log('ZoneState', `Deleted pin: ${id}`)
      this.notifyChange()
    } else {
      debug.warn('ZoneState', `Pin not found for deletion: ${id}`)
    }
    return deleted
  }

  /**
   * Get all pins
   */
  getPins(): ContextPin[] {
    return Array.from(this.pins.values())
  }

  /**
   * Get a pin by ID
   */
  getPinById(id: string): ContextPin | undefined {
    return this.pins.get(id)
  }

  // ============================================================================
  // Bulk operations
  // ============================================================================

  /**
   * Clear all zones and pins
   */
  clearAll(): void {
    this.zones.clear()
    this.pins.clear()
    debug.log('ZoneState', 'Cleared all zones and pins')
    this.notifyChange()
  }

  /**
   * Import state from serialized data
   */
  importState(state: ZoneState): void {
    this.zones.clear()
    this.pins.clear()

    for (const zone of state.zones) {
      this.zones.set(zone.id, zone)
      // Update nextId to avoid collisions
      const idNum = parseInt(zone.id.split('_')[1] || '0', 10)
      if (idNum >= nextId) {
        nextId = idNum + 1
      }
    }

    for (const pin of state.pins) {
      this.pins.set(pin.id, pin)
      // Update nextId to avoid collisions
      const idNum = parseInt(pin.id.split('_')[1] || '0', 10)
      if (idNum >= nextId) {
        nextId = idNum + 1
      }
    }

    debug.log('ZoneState', `Imported state: ${state.zones.length} zones, ${state.pins.length} pins`)
    this.notifyChange()
  }

  /**
   * Export current state
   */
  exportState(): ZoneState {
    return {
      zones: this.getZones(),
      pins: this.getPins(),
    }
  }

  // ============================================================================
  // Change tracking
  // ============================================================================

  /**
   * Subscribe to state changes
   */
  onChange(callback: ZoneStateChangeCallback): () => void {
    this.changeCallbacks.push(callback)
    return () => {
      const index = this.changeCallbacks.indexOf(callback)
      if (index >= 0) {
        this.changeCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Notify all subscribers of state change
   */
  private notifyChange(): void {
    const state = this.exportState()
    for (const callback of this.changeCallbacks) {
      try {
        callback(state)
      } catch (error) {
        debug.error('ZoneState', 'Error in change callback', error)
      }
    }
  }
}

// Singleton instance
export const zoneState = new ZoneStateManager()
